import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import * as Ably from 'ably';
import {
	AsyncSubject,
	BehaviorSubject,
	NEVER,
	Observable,
	Subject,
	asapScheduler,
	combineLatest,
	concat,
	firstValueFrom,
	of,
	scheduled,
	timer,
} from 'rxjs';
import {
	distinctUntilChanged,
	filter,
	finalize,
	map,
	mergeAll,
	startWith,
	switchMap,
	take,
	takeUntil,
	tap,
} from 'rxjs/operators';
import { Permits } from 'src/lib/constants/permissions';
import { getEnvironment } from 'src/lib/environment/environment';
import { LoginService } from '../api/generic/login/login.service';
import { UserActiveService } from '../startup/user-active/user-active.service';
import { OnlineStatus } from '../stores/online-status-store/online-status.enum';
import { PermissionStoreService } from '../stores/permission-store/permission-store.service';
import { ABLY_REALTIME, AblyRealtimeConstructor } from './ably-realtime.token';

const TOKEN_ENDPOINT = '/api/v1/gradchat/token';

type getChannelName = (type: 'user' | 'presence') => string | null;

@Injectable({
	providedIn: 'root',
})
export class WebsocketService implements OnDestroy {
	private static initialized = false;
	private _unsubscribe$ = new AsyncSubject<null>();

	private _isConnected = new BehaviorSubject<boolean>(false);

	private _ablyRealtime: Ably.Realtime;
	private _onConnect = new Subject<Ably.ConnectionStateChange>();
	private _onClose = new Subject<Ably.ConnectionStateChange>();
	private _onError = new Subject<Ably.ConnectionStateChange>();
	private _onSuspend = new Subject<Ably.ConnectionStateChange>();
	private _ablyIsRetryTime: number | null = null;

	get isConnected$() {
		return this._isConnected.asObservable();
	}

	get onConnect$() {
		return this._onConnect.asObservable();
	}
	get onClose$() {
		return this._onClose.asObservable();
	}
	get onError$() {
		return this._onError.asObservable();
	}
	get onSuspend$() {
		return this._onSuspend.asObservable();
	}

	private _userPublishSubject = new Subject<
		[topic: string, message: unknown]
	>();
	private _userMessageSubject = new Subject<Ably.Message>();

	private _userWatcherCount$ = new BehaviorSubject<number>(0);
	private _presenceWatcherCount$ = new BehaviorSubject<number>(0);

	private _connectionActivitySubject = new Subject<null>();
	private _connectionKeepAlive = this._connectionActivitySubject.pipe(
		switchMap(() => {
			const keepTime =
				this._presenceWatcherCount$.value > 0
					? getEnvironment().settings.services.websocket
							.connectionKeepAlivePresence
					: getEnvironment().settings.services.websocket.connectionKeepAlive;

			return concat(of(true), timer(keepTime).pipe(map(() => false)));
		}),
		startWith(false),
		distinctUntilChanged(),
	);

	private _connectionBump$ = new Subject<'bump'>();

	constructor(
		private httpClient: HttpClient,
		private permissionService: PermissionStoreService,
		private loginService: LoginService,
		private userActiveService: UserActiveService,
		@Inject(ABLY_REALTIME) private AblyRealtime: AblyRealtimeConstructor,
	) {
		this.initSubscriptions();
	}

	private initSubscriptions = () => {
		scheduled([this.onConnect$], asapScheduler)
			.pipe(mergeAll())
			.subscribe(() => {
				this._isConnected.next(true);
			});
		scheduled([this.onClose$, this.onError$], asapScheduler)
			.pipe(mergeAll())
			.subscribe(() => {
				this._isConnected.next(false);
			});
	};

	public init = () => {
		if (WebsocketService.initialized) {
			return;
		}
		WebsocketService.initialized = true;

		this.loginService
			.getLoginStatus()
			.pipe(
				filter((x) => x.uid != null),
				switchMap((login) =>
					this.permissionService.getFieldSet$().pipe(
						take(1),
						filter((x) =>
							x.canDo(Permits['ga_direct_message|gabby|participate gabby']),
						),
						map(() => login.uid),
					),
				),
			)
			.subscribe((uid) => {
				this.connectWebsocket(uid);
			});
	};

	private waitForConnection = async () => {
		this._connectionBump$.next('bump');

		await firstValueFrom(
			this.isConnected$.pipe(
				filter((x) => x),
				take(1),
			),
		);
	};

	private connectWebsocket = (uid: number) => {
		let token: Ably.TokenDetails = null;

		const ably = new this.AblyRealtime({
			autoConnect: false,
			authCallback: async (_tokenParams, callback) => {
				const loggedIn = await firstValueFrom(
					this.loginService.getLoginStatus(),
				);
				try {
					if (loggedIn?.uid === uid) {
						token = await firstValueFrom(
							this.httpClient.get<Ably.TokenDetails>(TOKEN_ENDPOINT, {
								withCredentials: true,
							}),
						);

						callback(null, token.token);
					} else {
						token = null;
						callback('User not logged in', null);
					}
				} catch {
					token = null;
					callback('Could not get token', null);
				}
			},
		});
		this._ablyRealtime = ably;

		const getChannelName: getChannelName = (
			type: 'user' | 'presence',
		): string | null => {
			const capabilities: Ably.TokenParams['capability'] = JSON.parse(
				token?.capability ?? '{}',
			);
			if (type === 'user') {
				for (const key of Object.keys(capabilities)) {
					if (key.startsWith('user.')) {
						return key;
					}
				}
			} else if (type === 'presence') {
				for (const key of Object.keys(capabilities)) {
					if (key.startsWith('presence.')) {
						return key;
					}
				}
			}
			return null;
		};

		this.watchSocketConnection();
		this.watchSocketMessages(getChannelName);
	};

	private watchSocketConnection = () => {
		this._ablyRealtime.connection.on((stateChange) => {
			switch (stateChange.current) {
				case 'connected':
					this._onConnect.next(stateChange);
					break;
				case 'disconnected':
				case 'closed':
					this._onClose.next(stateChange);
					break;
				case 'suspended':
					this._onSuspend.next(stateChange);
					break;
				case 'failed':
					this._onError.next(stateChange);
					break;
				case 'connecting':
				case 'closing':
				case 'initialized':
					// Do nothing for these states
					break;
				default:
					console.warn('Unknown Ably connection state', stateChange.current);
			}
		});
	};

	private watchSocketMessages = (getChannelName: getChannelName) => {
		// Manage connection state
		let isPresenceChannelSubscribed = false;
		let isUserChannelSubscribed = false;
		let firstConnectionEstablished = null;
		this.onClose$.pipe(takeUntil(this._unsubscribe$)).subscribe((change) => {
			if (change?.retryIn != null) {
				this._ablyIsRetryTime = change.retryIn;
				console.info(
					`Ably connection closed, retrying in ${change.retryIn} ms`,
				);
			}
		});

		this.isConnected$
			.pipe(takeUntil(this._unsubscribe$))
			.subscribe((isConnected) => {
				if (!isConnected) {
					isPresenceChannelSubscribed = false;
					isUserChannelSubscribed = false;
				} else {
					firstConnectionEstablished = true;
					this._ablyIsRetryTime = null;
				}
			});

		this.onError$.pipe(takeUntil(this._unsubscribe$)).subscribe((state) => {
			console.error('Ably connection error', state.reason);
			this._unsubscribe$.next(null); // This is an unrecoverable state, so we disable everything to prevent thrashing
		});

		combineLatest([
			this.userActiveService.isAway$.pipe(distinctUntilChanged()),
			this._userWatcherCount$.pipe(
				map((count) => count > 0),
				distinctUntilChanged(),
			),
			this._connectionKeepAlive,
			this.isConnected$,
			this._connectionBump$.pipe(
				startWith('init'),
				switchMap((x) => {
					if (x === 'bump') {
						return timer(1);
					} else {
						return of(null);
					}
				}),
			),
		])
			.pipe(
				distinctUntilChanged(),
				filter(() => firstConnectionEstablished !== false), // Don't attempt to connect if we've already determined we can't connect
				filter(() => this._ablyIsRetryTime == null), // Don't attempt to connect while ably is self retrying
				takeUntil(this._unsubscribe$),
			)
			.subscribe(
				async ([isAway, isWatching, subscriptionKeepAlive, isConnected]) => {
					if ((!isAway && isWatching) || subscriptionKeepAlive) {
						if (firstConnectionEstablished === null) {
							firstConnectionEstablished = false;
						}

						if (isConnected) return; // Already connected
						await this._ablyRealtime.connect();
					} else {
						if (!subscriptionKeepAlive) {
							await this._ablyRealtime.close();
						}
					}
				},
			);

		// Watch for messages to publish
		this._userPublishSubject.subscribe(async ([topic, message]) => {
			this._connectionActivitySubject.next(null); // Bump the keep-alive for outgoing messages as well since they indicate an active client
			await this.waitForConnection();
			const userChannelName = getChannelName('user');
			if (userChannelName != null) {
				const channel = this._ablyRealtime.channels.get(userChannelName);
				await channel.publish(topic, message);
			}
		});

		// Watch for user subscriptions
		combineLatest([this._userWatcherCount$, this.isConnected$])
			.pipe(
				switchMap(([count, isConnected]) => {
					if (count > 0) {
						return of([count, isConnected] as [number, boolean]);
					} else {
						return timer(
							getEnvironment().settings.services.websocket.connectionTimeout,
						).pipe(map(() => [count, isConnected] as [number, boolean]));
					}
				}),
			)
			.subscribe(async ([count, isConnected]) => {
				const channelName = getChannelName('user');

				try {
					if (count > 0 && isConnected) {
						if (channelName != null && !isUserChannelSubscribed) {
							await this._ablyRealtime.channels
								.get(channelName)
								.subscribe((msg) => {
									this._userMessageSubject.next(msg);
								});
							isUserChannelSubscribed = true;
						}
					} else if (channelName != null) {
						await this._ablyRealtime.channels.get(channelName).unsubscribe();
						isUserChannelSubscribed = false;
					}
				} catch {
					isUserChannelSubscribed = false;
				}
			});

		// Watch for presence subscriptions
		combineLatest([this._presenceWatcherCount$, this.isConnected$])
			.pipe(
				switchMap(([count, isConnected]) => {
					if (count > 0) {
						return of([count, isConnected] as [number, boolean]);
					} else {
						return timer(
							getEnvironment().settings.services.websocket.connectionTimeout,
						).pipe(map(() => [count, isConnected] as [number, boolean]));
					}
				}),
			)
			.subscribe(async ([count, isConnected]) => {
				const channelName = getChannelName('presence');

				try {
					if (count > 0 && isConnected) {
						if (channelName != null && !isPresenceChannelSubscribed) {
							isPresenceChannelSubscribed = true;
							await this._ablyRealtime.channels
								.get(channelName)
								.presence.enter(OnlineStatus.online);
						}
					} else if (isConnected && channelName != null) {
						isPresenceChannelSubscribed = false;
						await this._ablyRealtime.channels.get(channelName).presence.leave();
					}
				} catch {
					isPresenceChannelSubscribed = false;
				}
			});
	};

	//
	// Public API
	//
	public registerPresence$ = (): Observable<unknown> => {
		return of(null).pipe(
			tap(() => {
				this._presenceWatcherCount$.next(this._presenceWatcherCount$.value + 1);
			}),
			switchMap(() => NEVER),
			finalize(() => {
				this._presenceWatcherCount$.next(this._presenceWatcherCount$.value - 1);
			}),
		);
	};

	public publish$ = (topic: string, message: unknown): Observable<unknown> => {
		return new Observable((observer) => {
			(async () => {
				await this.waitForConnection();
				try {
					this._userPublishSubject.next([topic, message]);
					observer.next(null);
					observer.complete();
				} catch (error) {
					observer.error(error);
				}
			})();
		});
	};

	public subscribe$ = <T>(topic: string): Observable<T> => {
		return of(null).pipe(
			tap(() => {
				this._userWatcherCount$.next(this._userWatcherCount$.value + 1);
			}),
			switchMap(() => this._userMessageSubject),
			filter((msg) => msg.name === topic),
			tap(() => this._connectionActivitySubject.next(null)),
			map((msg) => msg.data as T),
			finalize(() => {
				this._userWatcherCount$.next(this._userWatcherCount$.value - 1);
			}),
		);
	};

	ngOnDestroy() {
		try {
			this._ablyRealtime?.close();
		} catch {
			// Do nothing.
		}

		this._unsubscribe$.next(null);
		this._unsubscribe$.complete();
		this._unsubscribe$ = null;
	}
}
